export interface CodeInputState {
  focus: number;
  value: string;
}

export type CodeInputAction =
  | { digit: string; index: number; type: "digit" }
  | { index: number; type: "backspace" }
  | { direction: -1 | 1; index: number; type: "arrow" }
  | { text: string; type: "paste" };

const LENGTH = 6;
const DIGIT = /\d/;
const SINGLE_DIGIT = /^\d$/;

const clamp = (index: number): number =>
  Math.max(0, Math.min(LENGTH - 1, index));

const slots = (value: string): string[] =>
  Array.from({ length: LENGTH }, (_, index) => {
    const char = value[index];
    return char && DIGIT.test(char) ? char : " ";
  });

export const emptyCode = (): string => " ".repeat(LENGTH);

export const codeDigits = (value: string): string =>
  value.replaceAll(/[^\d]/g, "");

export const applyCodeInput = (
  state: CodeInputState,
  action: CodeInputAction
): CodeInputState => {
  const digits = slots(state.value);
  switch (action.type) {
    case "digit": {
      if (!SINGLE_DIGIT.test(action.digit)) {
        return state;
      }
      const index = clamp(action.index);
      digits[index] = action.digit;
      return {
        focus: Math.min(index + 1, LENGTH - 1),
        value: digits.join(""),
      };
    }
    case "backspace": {
      const index = clamp(action.index);
      if (digits[index] !== " ") {
        digits[index] = " ";
        return { focus: index, value: digits.join("") };
      }
      if (index === 0) {
        return { focus: 0, value: digits.join("") };
      }
      digits[index - 1] = " ";
      return { focus: index - 1, value: digits.join("") };
    }
    case "arrow": {
      return {
        focus: clamp(action.index + action.direction),
        value: digits.join(""),
      };
    }
    case "paste": {
      const pasted = action.text.replaceAll(/\D/g, "").slice(0, LENGTH);
      if (!pasted) {
        return state;
      }
      const next = Array.from({ length: LENGTH }, () => " ");
      for (const [index, digit] of [...pasted].entries()) {
        next[index] = digit;
      }
      return {
        focus: Math.min(pasted.length, LENGTH - 1),
        value: next.join(""),
      };
    }
    default: {
      const unreachable: never = action;
      return unreachable;
    }
  }
};

const NON_DIGITS = /\D/g;

/** Turns a field edit or paste into the next six-box code. */
export const codeFromField = (
  value: string,
  index: number,
  raw: string
): CodeInputState => {
  const current = DIGIT.test(value[index] ?? "") ? value[index] : "";
  const incoming = raw.replaceAll(NON_DIGITS, "");
  if (incoming.length > 1) {
    const replacing =
      incoming.length === 2 &&
      current &&
      (incoming.startsWith(current) || incoming.endsWith(current));
    if (replacing) {
      const nextDigit = incoming.startsWith(current)
        ? incoming[1]
        : incoming[0];
      if (!nextDigit) {
        return { focus: index, value };
      }
      return applyCodeInput(
        { focus: index, value },
        { digit: nextDigit, index, type: "digit" }
      );
    }
    return applyCodeInput(
      { focus: index, value },
      { text: raw, type: "paste" }
    );
  }
  if (!incoming) {
    return { focus: index, value };
  }
  return applyCodeInput(
    { focus: index, value },
    { digit: incoming, index, type: "digit" }
  );
};
